import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/lang";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import Reveal from "@/components/ui-brand/Reveal";

/**
 * patrice.studio legal pages — Impressum, Datenschutz, AGB.
 * Inhalte basieren auf den vom Anbieter gelieferten SGB-Texten, angepasst
 * an die Umbrella-Brand patrice.studio (keine separate Kanzlei nötig — Anbieter-
 * Daten sind identisch, nur Domain/Branding ändert sich).
 */

const Section = ({ eyebrow, title, children }) => (
  <section className="border-t border-warmth/25 pt-10 mt-12 first:border-t-0 first:mt-0 first:pt-0">
    {eyebrow && (
      <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere mb-3">
        {eyebrow}
      </p>
    )}
    {title && (
      <h2 className="font-display text-2xl md:text-3xl leading-tight text-depth mb-6">
        {title}
      </h2>
    )}
    <div className="space-y-5 text-depth/90 font-light text-base md:text-[1.0625rem] leading-relaxed">
      {children}
    </div>
  </section>
);

const Sub = ({ children }) => (
  <h3 className="font-display italic text-xl md:text-2xl text-depth mt-6 mb-3">{children}</h3>
);

const Impressum = () => (
  <Section eyebrow="01 — Anbieter" title="Impressum">
    <p>
      <strong>Patrice Schreiter</strong>
      <br />
      Einzelunternehmerin
      <br />
      Ingelheimer Straße 9
      <br />
      09212 Limbach-Oberfrohna
    </p>
    <Sub>Kontakt</Sub>
    <p>
      Telefon: +49 (0) 3722 7981957
      <br />
      E-Mail:{" "}
      <a className="text-signal hover:underline" href="mailto:hallo@patrice.studio">
        hallo@patrice.studio
      </a>
    </p>
    <Sub>Umsatzsteuer-ID</Sub>
    <p>
      Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
      <br />
      <strong>DE455365728</strong>
    </p>
    <Sub>Verbraucherstreitbeilegung / Universalschlichtungsstelle</Sub>
    <p>
      Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
      Verbraucherschlichtungsstelle teilzunehmen.
    </p>
    <Sub>Marken & Inhalte</Sub>
    <p>
      patrice.studio ist die Dach-Marke der Einzelunternehmerin Patrice Schreiter und
      bündelt mehrere Projekte (u. a. Sichtbar Gut Bewertet). Sämtliche Texte, Fotos,
      Konzepte und visuellen Elemente auf dieser Website sind urheberrechtlich geschützt.
      Eine Weiterverwendung — auch auszugsweise — bedarf der ausdrücklichen schriftlichen
      Zustimmung.
    </p>
  </Section>
);

const Datenschutz = () => (
  <>
    <Section eyebrow="01 — Auf einen Blick" title="Datenschutzerklärung">
      <Sub>Allgemeine Hinweise</Sub>
      <p>
        Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
        personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene
        Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
      </p>
      <Sub>Datenerfassung auf dieser Website</Sub>
      <p>
        Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen
        Kontaktdaten finden Sie im Abschnitt „Hinweis zur verantwortlichen Stelle".
      </p>
      <p>
        Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen — z. B.
        über das Kontaktformular oder per E-Mail. Andere Daten werden automatisch oder nach
        Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst (technische
        Daten wie Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
      </p>
      <Sub>Welche Rechte haben Sie bezüglich Ihrer Daten?</Sub>
      <p>
        Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und
        Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem
        ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Erteilte
        Einwilligungen können Sie jederzeit für die Zukunft widerrufen.
      </p>
    </Section>

    <Section eyebrow="02 — Hosting" title="Wo läuft diese Website?">
      <p>
        Diese Website wird über die Emergent-Infrastruktur betrieben. Die Verwendung erfolgt
        auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse
        an einer zuverlässigen Darstellung. Soweit eine entsprechende Einwilligung abgefragt
        wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1
        lit. a DSGVO und § 25 Abs. 1 TDDDG.
      </p>
      <Sub>Auftragsverarbeitung</Sub>
      <p>
        Mit dem Hosting-Anbieter wurde ein Vertrag über Auftragsverarbeitung (AVV)
        geschlossen, der gewährleistet, dass personenbezogene Daten unserer
        Websitebesucherinnen und -besucher nur nach unseren Weisungen und unter Einhaltung
        der DSGVO verarbeitet werden.
      </p>
      <Sub>E-Mail-Versand (SMTP2GO)</Sub>
      <p>
        Für den Versand transaktionaler E-Mails (Kontaktformular, Buchungsbestätigung,
        Newsletter-Willkommen) nutzen wir den Dienst SMTP2GO. Die übermittelten Daten
        (Name, E-Mail-Adresse, Nachricht) werden ausschließlich zur Bearbeitung Ihrer
        Anfrage verarbeitet. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO bzw. Ihr
        berechtigtes Interesse an einer effizienten Kommunikation (Art. 6 Abs. 1 lit. f
        DSGVO).
      </p>
    </Section>

    <Section eyebrow="03 — Pflichtinformationen" title="Allgemeine Hinweise">
      <Sub>Hinweis zur verantwortlichen Stelle</Sub>
      <p>
        Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
        <br />
        Patrice Schreiter · Einzelunternehmerin
        <br />
        Ingelheimer Straße 9 · 09212 Limbach-Oberfrohna
        <br />
        Telefon: +49 (0) 3722 7981957
        <br />
        E-Mail:{" "}
        <a className="text-signal hover:underline" href="mailto:hallo@patrice.studio">
          hallo@patrice.studio
        </a>
      </p>
      <Sub>Speicherdauer</Sub>
      <p>
        Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer
        genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck
        für die Datenverarbeitung entfällt oder Sie eine Einwilligung widerrufen.
      </p>
      <Sub>Widerruf Ihrer Einwilligung</Sub>
      <p>
        Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung
        möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Die
        Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf
        unberührt.
      </p>
      <Sub>Beschwerderecht bei der Aufsichtsbehörde</Sub>
      <p>
        Im Falle von Verstößen gegen die DSGVO steht Betroffenen ein Beschwerderecht bei
        einer Aufsichtsbehörde zu — insbesondere in dem Mitgliedstaat ihres gewöhnlichen
        Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes.
      </p>
      <Sub>Auskunft, Berichtigung und Löschung</Sub>
      <p>
        Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten
        personenbezogenen Daten sowie ggf. ein Recht auf Berichtigung oder Löschung. Hierzu
        können Sie sich jederzeit an uns wenden.
      </p>
    </Section>

    <Section eyebrow="04 — Datenerfassung" title="Was auf dieser Website konkret passiert">
      <Sub>Kontakt- und Buchungsformular</Sub>
      <p>
        Wenn Sie uns über das Kontaktformular oder das Buchungsformular eine Anfrage
        schicken, werden Ihre Angaben inklusive der Kontaktdaten zwecks Bearbeitung der
        Anfrage und für eventuelle Anschlussfragen gespeichert. Diese Daten geben wir nicht
        ohne Ihre Einwilligung weiter.
      </p>
      <p>
        Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO bei
        vorvertraglichen Maßnahmen oder Vertragsdurchführung, ansonsten auf Grundlage
        unseres berechtigten Interesses (Art. 6 Abs. 1 lit. f DSGVO) oder Ihrer Einwilligung
        (Art. 6 Abs. 1 lit. a DSGVO).
      </p>
      <Sub>Anfrage per E-Mail oder WhatsApp</Sub>
      <p>
        Wenn Sie mich per E-Mail oder über andere auf der Website angegebene
        Kontaktmöglichkeiten (z. B. WhatsApp) kontaktieren, werden die übermittelten Daten
        ausschließlich zur Bearbeitung Ihrer Anfrage gespeichert und verarbeitet.
      </p>
      <p>
        Bei WhatsApp erfolgt die Kommunikation über die WhatsApp Ireland Limited
        (Meta Platforms Inc.). Dabei können personenbezogene Daten auch auf Servern in
        Staaten außerhalb der Europäischen Union übertragen werden.
      </p>
      <p className="text-atmosphere/70 text-sm pt-6">Basisstruktur: e-recht24.de</p>
    </Section>
  </>
);

const AGB = () => (
  <>
    <Section eyebrow="Allgemeine Geschäftsbedingungen" title="patrice.studio — für die Buchung von Dienstleistungen">
      <p>
        Patrice Schreiter
        <br />
        Ingelheimer Straße 9 · 09212 Limbach-Oberfrohna
        <br />
        E-Mail: hallo@patrice.studio · Telefon: +49 (0) 3722 7981957
      </p>
    </Section>

    <Section eyebrow="§ 1" title="Geltungsbereich">
      <p>
        Diese AGB gelten für sämtliche über die Website <strong>www.patrice.studio</strong>{" "}
        angebotenen Dienstleistungen (Beratung, Brand- und Storytelling-Arbeit, kreative
        Konzeption, Webdesign, individuelle Pakete). Sie gelten ausschließlich; abweichende
        AGB des Kunden werden nur dann Vertragsbestandteil, wenn die Anbieterin ihnen
        ausdrücklich zustimmt.
      </p>
    </Section>

    <Section eyebrow="§ 2" title="Vertragsschluss">
      <p>
        2.1 Die Darstellung der Dienstleistungen auf der Website ist kein verbindliches
        Angebot, sondern eine Aufforderung an Besucherinnen und Besucher, ein Angebot
        abzugeben.
      </p>
      <p>
        2.2 Die Anfrage erfolgt über die auf der Website bereitgestellten Formulare oder
        per E-Mail. Mit Absenden gibt die Kundin / der Kunde ein verbindliches Angebot ab.
        Der Vertragsschluss erfolgt durch Annahme der Anbieterin in Textform (z. B. per
        E-Mail).
      </p>
      <p>
        2.3 Eingaben können vor verbindlicher Abgabe jederzeit korrigiert werden. Nach
        Vertragsschluss wird der Vertragstext gespeichert und in Textform übermittelt.
      </p>
    </Section>

    <Section eyebrow="§ 3" title="Widerrufsrecht für Verbraucher">
      <p>
        Verbraucherinnen und Verbrauchern steht bei Fernabsatzverträgen grundsätzlich ein
        Widerrufsrecht zu. Verbraucher ist jede natürliche Person, die ein Rechtsgeschäft
        zu Zwecken abschließt, die überwiegend weder ihrer gewerblichen noch ihrer
        selbstständigen beruflichen Tätigkeit zugerechnet werden können.
      </p>
      <Sub>Hinweis zum Kundenkreis</Sub>
      <p>
        Dieses Angebot richtet sich vornehmlich an Unternehmerinnen und Unternehmer
        im Sinne des § 14 BGB. Sollte in Einzelfällen ein Vertrag mit Verbraucherinnen
        und Verbrauchern zustande kommen, beginnt die Ausführung — auf ausdrücklichen
        Wunsch — unmittelbar nach Bestellung. Mit Beginn der Ausführung kann ein
        eventuell bestehendes Widerrufsrecht gemäß § 356 Abs. 4 BGB vorzeitig erlöschen.
      </p>
    </Section>

    <Section eyebrow="§ 4" title="Vergütung & Zahlungsbedingungen">
      <p>
        4.1 Es gelten die zum Zeitpunkt des Vertragsschlusses individuell vereinbarten
        Vergütungen. Alle Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer,
        sofern nicht ausdrücklich anders ausgewiesen.
      </p>
      <p>
        4.2 Die Zahlung erfolgt — sofern nicht anders vereinbart — per Banküberweisung
        innerhalb der auf der Rechnung genannten Zahlungsfrist. Weitere Zahlungsmittel
        können nach Absprache angeboten werden.
      </p>
      <p>
        4.3 Bei Abschlagszahlungen oder vereinbarter Vorauszahlung beginnt die
        Leistungserbringung erst nach Zahlungseingang.
      </p>
    </Section>

    <Section eyebrow="§ 5" title="Leistungserbringung & Mitwirkung">
      <p>
        5.1 Die Anbieterin erbringt die vereinbarten Dienstleistungen mit der erforderlichen
        Sorgfalt und Fachkenntnis. Konkrete Erfolge oder bestimmte messbare Ergebnisse
        werden — sofern nicht ausdrücklich schriftlich zugesichert — nicht geschuldet.
      </p>
      <p>
        5.2 Der Kunde stellt alle für die Leistungserbringung notwendigen Informationen,
        Zugänge und Materialien rechtzeitig und in geeigneter Form zur Verfügung.
        Verzögerungen, die durch fehlende Mitwirkung des Kunden entstehen, gehen nicht
        zu Lasten der Anbieterin.
      </p>
    </Section>

    <Section eyebrow="§ 6" title="Haftung & Freistellung">
      <p>
        6.1 Die Anbieterin haftet unbeschränkt für Schäden aus der Verletzung des Lebens,
        des Körpers oder der Gesundheit sowie bei vorsätzlicher oder grob fahrlässiger
        Pflichtverletzung. Ebenso bei Garantieversprechen und zwingender Haftung.
      </p>
      <p>
        6.2 Bei leichter Fahrlässigkeit haftet die Anbieterin nur bei Verletzung
        wesentlicher Vertragspflichten und nur in Höhe des vertragstypischen, vorhersehbaren
        Schadens.
      </p>
      <p>
        6.3 Im Übrigen ist eine Haftung ausgeschlossen. Der Kunde stellt die Anbieterin
        von jeglichen Ansprüchen Dritter frei, die gegen sie aufgrund von rechts- oder
        vertragswidrigen Handlungen des Kunden geltend gemacht werden.
      </p>
    </Section>

    <Section eyebrow="§ 7" title="Urheber- und Nutzungsrechte">
      <p>
        7.1 Alle im Rahmen der Dienstleistung erstellten Werke (Texte, Konzepte, Designs,
        Bildmaterial) unterliegen dem Urheberrecht der Anbieterin.
      </p>
      <p>
        7.2 Mit vollständiger Zahlung erhält der Kunde ein einfaches, nicht ausschließliches
        Nutzungsrecht für den vereinbarten Zweck. Eine Weitergabe, Bearbeitung oder
        Veröffentlichung über den vereinbarten Rahmen hinaus bedarf der schriftlichen
        Zustimmung der Anbieterin.
      </p>
    </Section>

    <Section eyebrow="§ 8" title="Datenschutz">
      <p>
        Die Anbieterin behandelt die personenbezogenen Daten ihrer Kunden vertraulich und
        im Einklang mit den gesetzlichen Datenschutzvorschriften. Näheres entnehmen Sie der{" "}
        <Link to="/datenschutz" className="text-signal hover:underline">
          Datenschutzerklärung
        </Link>
        .
      </p>
    </Section>

    <Section eyebrow="§ 9" title="Schlussbestimmungen">
      <p>
        9.1 Anwendbar ist das Recht der Bundesrepublik Deutschland unter Ausschluss des
        UN-Kaufrechts, soweit diese Rechtswahl nicht dazu führt, dass ein Verbraucher mit
        gewöhnlichem Aufenthalt in der EU zwingenden gesetzlichen Bestimmungen seines
        Aufenthaltsstaates entzogen wird.
      </p>
      <p>
        9.2 Ist der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder
        öffentlich-rechtliches Sondervermögen, ist das Gericht am Sitz der Anbieterin
        zuständig.
      </p>
      <p>
        9.3 Sollte eine Bestimmung dieses Vertrages ungültig oder undurchsetzbar sein oder
        werden, bleiben die übrigen Bestimmungen hiervon unberührt.
      </p>
    </Section>

    <Section eyebrow="§ 10" title="Online-Streitbeilegung / Verbraucherschlichtung">
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </Section>
  </>
);

const TITLES = {
  imprint: { title: "Impressum", eyebrow: "— Pflichtangaben" },
  privacy: { title: "Datenschutzerklärung", eyebrow: "— DSGVO" },
  terms: { title: "Allgemeine Geschäftsbedingungen", eyebrow: "— AGB" },
};

const LegalPage = ({ kind = "imprint" }) => {
  const meta = TITLES[kind] || TITLES.imprint;
  useDocumentMeta({
    title: `${meta.title} · patrice.studio`,
    description: `${meta.title} der Marke patrice.studio · Patrice Schreiter`,
  });

  return (
    <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 max-w-4xl">
      <Reveal>
        <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere">
          {meta.eyebrow}
        </p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-6">
          {meta.title}
        </h1>
      </Reveal>
      <Reveal delay={140}>
        <div className="mt-12 md:mt-16">
          {kind === "imprint" && <Impressum />}
          {kind === "privacy" && <Datenschutz />}
          {kind === "terms" && <AGB />}
        </div>
      </Reveal>
    </section>
  );
};

export default LegalPage;
